import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-hot-toast";
import adminService from "../../services/adminService";

/* ---------- Helpers ---------- */
const money = (n) =>
  new Intl.NumberFormat("fr-MA", { style: "currency", currency: "MAD", minimumFractionDigits: 2 }).format(
    Number(n || 0)
  );

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

const TYPE_ICON = {
  hotel: "🏨",
  circuit: "🗺️",
  transport: "🚌",
  visite: "👁️",
  voiture: "🚗",
  circuit_sur_mesure: "✨",
};

const STATUS_META = {
  pending: { label: "En attente", cls: "bg-amber-100 text-amber-700" },
  paid: { label: "Payé", cls: "bg-emerald-100 text-emerald-700" },
  failed: { label: "Échoué", cls: "bg-red-100 text-red-700" },
  refunded: { label: "Remboursé", cls: "bg-slate-200 text-slate-700" },
  partially_refunded: { label: "Part. remboursé", cls: "bg-blue-100 text-blue-700" },
  voided: { label: "Annulé", cls: "bg-slate-200 text-slate-700" },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, cls: "bg-slate-100 text-slate-600" };
  return <span className={`px-2.5 py-1 inline-flex text-[11px] leading-5 font-bold rounded-full ${m.cls}`}>{m.label}</span>;
};

/* ---------- Facture premium (HTML imprimable) ---------- */
const buildInvoiceHtml = (o) => {
  const ref = (o.oid || "").toUpperCase();
  const lines = [];
  const p = o.payload || {};
  if (p.dateDepart || p.dateArrivee) lines.push(["Date", formatDate(p.dateDepart || p.dateArrivee)]);
  if (p.nbAdultes || p.nbPersonnes || p.participants)
    lines.push(["Participants", String(p.nbAdultes || p.nbPersonnes || p.participants)]);
  const refundsRows = (o.refunds || [])
    .map(
      (r) =>
        `<tr><td>${formatDate(r.date)}</td><td>${r.type === "void" ? "Annulation" : r.type === "total" ? "Total" : "Partiel"}</td><td>${
          r.method === "cmi_api" ? "Auto CMI" : "Manuel"
        }</td><td style="text-align:right">- ${money(r.amount)}</td></tr>`
    )
    .join("");

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"/>
<title>Facture ${ref}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;font-family:-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif}
  body{background:#f1f5f9;color:#0f172a;padding:32px}
  .sheet{max-width:780px;margin:0 auto;background:#fff;border-radius:24px;overflow:hidden;box-shadow:0 20px 60px rgba(15,23,42,.12)}
  .head{background:linear-gradient(120deg,#0f172a,#b45309);color:#fff;padding:34px 40px;display:flex;justify-content:space-between;align-items:flex-start}
  .brand{font-size:13px;letter-spacing:.18em;text-transform:uppercase;color:#fcd9a8;font-weight:700}
  .brand b{display:block;font-size:26px;letter-spacing:0;margin-top:4px;color:#fff}
  .doc{ text-align:right }
  .doc h1{font-size:30px;letter-spacing:.06em}
  .doc small{opacity:.85}
  .body{padding:34px 40px}
  .grid{display:flex;gap:24px;flex-wrap:wrap;margin-bottom:26px}
  .card{flex:1;min-width:220px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:16px;padding:16px 18px}
  .card h4{font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:#94a3b8;margin-bottom:8px}
  .card p{font-size:14px;line-height:1.5}
  table{width:100%;border-collapse:collapse;margin-top:6px}
  th,td{padding:12px 10px;font-size:13px;border-bottom:1px solid #e2e8f0;text-align:left}
  th{font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#94a3b8}
  .totals{margin-top:20px;margin-left:auto;width:300px}
  .totals .row{display:flex;justify-content:space-between;padding:8px 0;font-size:14px}
  .totals .grand{border-top:2px solid #0f172a;margin-top:6px;padding-top:12px;font-size:20px;font-weight:800}
  .status{display:inline-block;padding:6px 14px;border-radius:999px;font-weight:700;font-size:12px;background:#dcfce7;color:#15803d}
  .foot{padding:22px 40px;background:#f8fafc;border-top:1px solid #e2e8f0;font-size:12px;color:#64748b;text-align:center}
  .btns{max-width:780px;margin:18px auto 0;text-align:center}
  .btns button{background:#0f172a;color:#fff;border:0;padding:12px 22px;border-radius:12px;font-weight:700;cursor:pointer;font-size:14px}
  @media print{body{background:#fff;padding:0}.sheet{box-shadow:none;border-radius:0}.btns{display:none}}
</style></head>
<body>
  <div class="sheet">
    <div class="head">
      <div class="brand">Palais Touristique<b>🏛️ Nouzha</b></div>
      <div class="doc"><h1>FACTURE</h1><small>N° ${ref}</small><br/><small>${formatDate(o.createdAt)}</small></div>
    </div>
    <div class="body">
      <div class="grid">
        <div class="card"><h4>Facturé à</h4><p><b>${o.clientName || "Client"}</b><br/>${o.email || ""}<br/>${
    p.phone || ""
  }<br/>${p.address || ""}</p></div>
        <div class="card"><h4>Détails paiement</h4><p>Référence: <b>${ref}</b><br/>Méthode: <b>CMI</b><br/>Statut: <span class="status">${
    (STATUS_META[o.status] || {}).label || o.status
  }</span></p></div>
      </div>
      <table>
        <thead><tr><th>Description</th><th>Infos</th><th style="text-align:right">Montant</th></tr></thead>
        <tbody>
          <tr>
            <td><b>${TYPE_ICON[o.type] || "•"} ${o.typeLabel || o.type}</b></td>
            <td>${lines.map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}</td>
            <td style="text-align:right"><b>${money(o.amount)}</b></td>
          </tr>
        </tbody>
      </table>
      ${
        refundsRows
          ? `<h4 style="margin:22px 0 4px;font-size:12px;text-transform:uppercase;letter-spacing:.06em;color:#94a3b8">Remboursements</h4>
      <table><thead><tr><th>Date</th><th>Type</th><th>Mode</th><th style="text-align:right">Montant</th></tr></thead><tbody>${refundsRows}</tbody></table>`
          : ""
      }
      <div class="totals">
        <div class="row"><span>Sous-total</span><span>${money(o.amount)}</span></div>
        ${o.refundedAmount ? `<div class="row" style="color:#b91c1c"><span>Remboursé</span><span>- ${money(o.refundedAmount)}</span></div>` : ""}
        <div class="row grand"><span>Net encaissé</span><span>${money((o.amount || 0) - (o.refundedAmount || 0))}</span></div>
      </div>
    </div>
    <div class="foot">Palais Touristique — Place Atlas, 7 Rue Hassan Dkhissi, Fès 30000 · Contact@palaistouristique.ma · 06 66 03 56 57<br/>Paiement sécurisé par CMI · Merci de votre confiance.</div>
  </div>
  <div class="btns"><button onclick="window.print()">🖨️ Imprimer / Télécharger PDF</button></div>
</body></html>`;
};

const openInvoice = (order) => {
  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) {
    toast.error("Autorisez les fenêtres popup pour afficher la facture.");
    return;
  }
  w.document.open();
  w.document.write(buildInvoiceHtml(order));
  w.document.close();
};

/* ---------- View switcher ---------- */
const VIEW_OPTIONS = [
  { key: "cards", label: "Cartes", icon: "▦" },
  { key: "table", label: "Tableau", icon: "☰" },
  { key: "compact", label: "Compact", icon: "≣" },
];

/* ---------- Composant principal ---------- */
const PaymentsManager = () => {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [filters, setFilters] = useState({ status: "tous", type: "tous", search: "", page: 1 });
  const [selected, setSelected] = useState(null);
  const [viewMode, setViewModeState] = useState(() => {
    try {
      return localStorage.getItem("adminViewMode") || "cards";
    } catch {
      return "cards";
    }
  });
  const setViewMode = (v) => {
    setViewModeState(v);
    try {
      localStorage.setItem("adminViewMode", v);
    } catch (_) {}
  };

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.getPayments(filters);
      const d = res.data || {};
      setPayments(d.payments || []);
      setStats(d.stats || null);
      setPagination(d.pagination || { page: 1, totalPages: 1 });
    } catch (e) {
      toast.error(e.message || "Erreur de chargement des paiements");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const ViewSwitcher = () => (
    <div className="inline-flex items-center gap-1 bg-slate-100 rounded-xl p-1">
      {VIEW_OPTIONS.map((o) => (
        <button
          key={o.key}
          onClick={() => setViewMode(o.key)}
          title={o.label}
          className={`px-2.5 py-1.5 rounded-lg text-sm font-semibold transition-all ${
            viewMode === o.key ? "bg-white text-slate-800 shadow-sm" : "text-slate-400 hover:text-slate-600"
          }`}
        >
          <span className="mr-1">{o.icon}</span>
          <span className="hidden sm:inline">{o.label}</span>
        </button>
      ))}
    </div>
  );

  const renderActions = (o) => (
    <>
      <button
        onClick={() => setSelected(o)}
        className="text-indigo-700 px-3 py-2 rounded-lg border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 transition-all text-xs font-semibold"
      >
        👁️ Détails
      </button>
      <button
        onClick={() => openInvoice(o)}
        className="text-amber-700 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-all text-xs font-semibold"
      >
        🧾 Facture
      </button>
    </>
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2">💳 Paiements</h2>
            <p className="text-sm text-slate-500">Tous les règlements CMI — remboursements & factures</p>
          </div>
          <ViewSwitcher />
        </div>

        {/* Stat cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
            <div className="rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 text-white p-4">
              <p className="text-[11px] uppercase tracking-wide text-white/60 font-bold">Transactions</p>
              <p className="text-2xl font-black mt-1">{stats.totalCount}</p>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white p-4">
              <p className="text-[11px] uppercase tracking-wide text-white/70 font-bold">Encaissé</p>
              <p className="text-xl font-black mt-1">{money(stats.paidAmount)}</p>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-red-500 to-rose-700 text-white p-4">
              <p className="text-[11px] uppercase tracking-wide text-white/70 font-bold">Remboursé</p>
              <p className="text-xl font-black mt-1">{money(stats.refundedAmount)}</p>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white p-4">
              <p className="text-[11px] uppercase tracking-wide text-white/70 font-bold">Payés</p>
              <p className="text-2xl font-black mt-1">{stats.byStatus?.paid?.count || 0}</p>
            </div>
          </div>
        )}

        {/* Filtres */}
        <div className="flex flex-wrap gap-3 mt-5">
          <input
            type="text"
            placeholder="🔎 Rechercher (réf, email, nom, tél)…"
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value, page: 1 }))}
            className="flex-1 min-w-[220px] px-4 py-2.5 border-2 border-slate-200 rounded-xl focus:border-amber-400 outline-none text-sm"
          />
          <select
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, page: 1 }))}
            className="px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white"
          >
            <option value="tous">Tous statuts</option>
            <option value="paid">Payé</option>
            <option value="pending">En attente</option>
            <option value="failed">Échoué</option>
            <option value="partially_refunded">Part. remboursé</option>
            <option value="refunded">Remboursé</option>
            <option value="voided">Annulé</option>
          </select>
          <select
            value={filters.type}
            onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value, page: 1 }))}
            className="px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white"
          >
            <option value="tous">Tous services</option>
            <option value="circuit">Circuit</option>
            <option value="hotel">Hôtel</option>
            <option value="transport">Transport</option>
            <option value="visite">Visite</option>
            <option value="voiture">Voiture</option>
            <option value="circuit_sur_mesure">Circuit sur mesure</option>
          </select>
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <style>{`.nz-skel{position:relative;overflow:hidden;background:#eef2f7}.nz-skel::after{content:"";position:absolute;inset:0;transform:translateX(-100%);background:linear-gradient(90deg,transparent,rgba(255,255,255,.7),transparent);animation:nz-shimmer 1.4s infinite}@keyframes nz-shimmer{100%{transform:translateX(100%)}}`}</style>
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-2xl border border-slate-100 p-5 space-y-3">
                <div className="h-4 w-2/3 rounded nz-skel" />
                <div className="h-3 w-1/2 rounded nz-skel" />
                <div className="h-6 w-1/3 rounded nz-skel" />
              </div>
            ))}
          </div>
        ) : payments.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-5xl mb-3">💳</div>
            <p className="text-slate-500">Aucun paiement trouvé.</p>
          </div>
        ) : viewMode === "table" ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/80">
                <tr>
                  {["Référence", "Service", "Client", "Montant", "Statut", "Date", "Actions"].map((h) => (
                    <th key={h} className="px-5 py-3.5 text-left text-[11px] font-bold text-slate-400 uppercase tracking-[0.08em]">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {payments.map((o) => (
                  <tr key={o._id} className="hover:bg-orange-50/40 transition-colors">
                    <td className="px-5 py-4 font-mono text-xs text-slate-500">{o.oid}</td>
                    <td className="px-5 py-4 text-sm font-semibold text-slate-700">
                      {TYPE_ICON[o.type] || "•"} {o.typeLabel}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600">
                      <div className="font-medium">{o.clientName}</div>
                      <div className="text-xs text-slate-400">{o.email}</div>
                    </td>
                    <td className="px-5 py-4 font-bold text-slate-800">{money(o.amount)}</td>
                    <td className="px-5 py-4"><StatusBadge status={o.status} /></td>
                    <td className="px-5 py-4 text-xs text-slate-400">{formatDate(o.createdAt)}</td>
                    <td className="px-5 py-4"><div className="flex gap-2">{renderActions(o)}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : viewMode === "compact" ? (
          <div className="divide-y divide-slate-100">
            {payments.map((o) => (
              <div key={o._id} className="flex items-center gap-3 px-4 lg:px-6 py-3 hover:bg-orange-50/40 transition-colors">
                <div className="w-10 h-10 flex-none rounded-xl bg-slate-100 flex items-center justify-center text-lg">{TYPE_ICON[o.type] || "•"}</div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 text-sm truncate">{o.clientName} · {o.typeLabel}</p>
                  <p className="text-xs text-slate-400 truncate font-mono">{o.oid}</p>
                </div>
                <span className="font-bold text-slate-800 text-sm">{money(o.amount)}</span>
                <StatusBadge status={o.status} />
                <div className="flex gap-1.5 flex-none">{renderActions(o)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 lg:p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5">
            {payments.map((o) => (
              <div
                key={o._id}
                className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 p-5 flex flex-col"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 flex-none rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center text-lg">
                      {TYPE_ICON[o.type] || "•"}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-slate-800 truncate">{o.clientName}</p>
                      <p className="text-xs text-slate-400 truncate">{o.typeLabel}</p>
                    </div>
                  </div>
                  <StatusBadge status={o.status} />
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-black text-slate-800">{money(o.amount)}</p>
                    {o.refundedAmount > 0 && <p className="text-xs text-red-600 font-semibold">- {money(o.refundedAmount)} remboursé</p>}
                  </div>
                  <p className="text-[11px] text-slate-400 font-mono text-right">{o.oid?.slice(-10)}</p>
                </div>
                <p className="mt-1 text-xs text-slate-400">{formatDate(o.createdAt)}</p>
                <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-slate-100">{renderActions(o)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            disabled={filters.page <= 1}
            onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
            className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-sm font-semibold disabled:opacity-40"
          >
            ← Précédent
          </button>
          <span className="px-4 py-2 text-sm text-slate-500">
            {pagination.page} / {pagination.totalPages}
          </span>
          <button
            disabled={filters.page >= pagination.totalPages}
            onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
            className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-sm font-semibold disabled:opacity-40"
          >
            Suivant →
          </button>
        </div>
      )}

      {selected && (
        <PaymentDetailModal
          order={selected}
          onClose={() => setSelected(null)}
          onRefunded={() => {
            setSelected(null);
            fetchPayments();
          }}
        />
      )}
    </div>
  );
};

/* ---------- Modal détail + remboursement ---------- */
const PaymentDetailModal = ({ order, onClose, onRefunded }) => {
  const [detail, setDetail] = useState(order);
  const [mode, setMode] = useState("partial");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    adminService
      .getPaymentDetail(order._id || order.oid)
      .then((res) => alive && res?.data && setDetail(res.data))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [order]);

  const remaining = Math.max(0, (detail.amount || 0) - (detail.refundedAmount || 0));
  const canRefund = ["paid", "partially_refunded"].includes(detail.status) && remaining > 0;

  const submitRefund = async () => {
    const body = { mode, reason };
    if (mode === "partial") {
      const amt = Number(amount);
      if (!Number.isFinite(amt) || amt <= 0) return toast.error("Saisissez un montant valide.");
      if (amt > remaining + 0.01) return toast.error(`Max remboursable: ${money(remaining)}`);
      body.amount = amt;
    }
    if (!window.confirm(`Confirmer le remboursement ${mode === "void" ? "(annulation totale)" : mode === "total" ? `de ${money(remaining)}` : `de ${money(Number(amount))}`} ?`))
      return;

    setSubmitting(true);
    try {
      const res = await adminService.refundPayment(detail._id || detail.oid, body);
      if (res.auto) toast.success(res.message);
      else
        toast(
          (t) => (
            <span className="text-sm">
              {res.message}
              <br />
              <b>À confirmer dans le back-office lanacash.</b>
            </span>
          ),
          { duration: 7000, icon: "⚠️" }
        );
      onRefunded();
    } catch (e) {
      toast.error(e.message || "Erreur lors du remboursement");
    } finally {
      setSubmitting(false);
    }
  };

  const p = detail.payload || {};

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[70] p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden my-8 flex flex-col">
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-5 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-black text-white flex items-center gap-2">
              {TYPE_ICON[detail.type] || "💳"} {detail.typeLabel}
            </h3>
            <p className="text-white/60 text-xs font-mono mt-1">{detail.oid}</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-2xl leading-none">×</button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5">
          <div className="flex items-center justify-between">
            <StatusBadge status={detail.status} />
            <button
              onClick={() => openInvoice(detail)}
              className="text-amber-700 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-all text-xs font-bold"
            >
              🧾 Voir / Imprimer la facture
            </button>
          </div>

          {/* Montants */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
              <p className="text-[10px] uppercase font-bold text-slate-400">Montant</p>
              <p className="text-lg font-black text-slate-800">{money(detail.amount)}</p>
            </div>
            <div className="rounded-2xl bg-red-50 border border-red-100 p-4">
              <p className="text-[10px] uppercase font-bold text-red-400">Remboursé</p>
              <p className="text-lg font-black text-red-700">{money(detail.refundedAmount)}</p>
            </div>
            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
              <p className="text-[10px] uppercase font-bold text-emerald-500">Restant</p>
              <p className="text-lg font-black text-emerald-700">{money(remaining)}</p>
            </div>
          </div>

          {/* Client */}
          <div className="rounded-2xl border border-slate-100 p-4">
            <h4 className="text-xs font-bold uppercase text-slate-400 mb-2">Client</h4>
            <div className="text-sm text-slate-700 space-y-1">
              <p>👤 {detail.clientName}</p>
              {detail.email && <p>✉️ {detail.email}</p>}
              {p.phone && <p>📞 {p.phone}</p>}
              {p.address && <p>📍 {p.address}</p>}
              <p className="text-xs text-slate-400 pt-1">Créé le {formatDate(detail.createdAt)}</p>
            </div>
          </div>

          {/* Historique remboursements */}
          {detail.refunds?.length > 0 && (
            <div className="rounded-2xl border border-slate-100 p-4">
              <h4 className="text-xs font-bold uppercase text-slate-400 mb-2">Historique des remboursements</h4>
              <ul className="space-y-2">
                {detail.refunds.map((r, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">
                      {formatDate(r.date)} · {r.method === "cmi_api" ? "Auto CMI" : "Manuel"}
                      {r.status === "pending_manual" && <span className="ml-2 text-amber-600 text-xs font-bold">(à confirmer)</span>}
                    </span>
                    <span className="font-bold text-red-600">- {money(r.amount)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Remboursement */}
          {canRefund ? (
            <div className="rounded-2xl border-2 border-dashed border-amber-200 bg-amber-50/40 p-4 space-y-3">
              <h4 className="text-sm font-bold text-slate-800">💸 Effectuer un remboursement</h4>
              <div className="flex gap-2">
                {[
                  { k: "partial", l: "Partiel" },
                  { k: "total", l: "Total" },
                  { k: "void", l: "Annuler" },
                ].map((m) => (
                  <button
                    key={m.k}
                    onClick={() => setMode(m.k)}
                    className={`flex-1 px-3 py-2 rounded-xl text-sm font-bold border transition-all ${
                      mode === m.k ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"
                    }`}
                  >
                    {m.l}
                  </button>
                ))}
              </div>
              {mode === "partial" && (
                <input
                  type="number"
                  min="0"
                  max={remaining}
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`Montant (max ${remaining.toFixed(2)})`}
                  className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:border-amber-400 outline-none"
                />
              )}
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Motif (optionnel)"
                className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:border-amber-400 outline-none"
              />
              <button
                onClick={submitRefund}
                disabled={submitting}
                className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-red-500 to-rose-600 text-white font-bold hover:from-red-600 hover:to-rose-700 transition-all disabled:opacity-50"
              >
                {submitting ? "Traitement…" : mode === "void" ? "Annuler le paiement" : "Rembourser"}
              </button>
              <p className="text-[11px] text-slate-500">
                ℹ️ Tente l'API CMI automatiquement. Si indisponible, le remboursement est enregistré et à confirmer dans le back-office lanacash.
              </p>
            </div>
          ) : (
            <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4 text-sm text-slate-500 text-center">
              {detail.status === "refunded"
                ? "Ce paiement est entièrement remboursé."
                : detail.status === "voided"
                ? "Ce paiement a été annulé."
                : "Remboursement indisponible (paiement non réglé)."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentsManager;
